// src/utils/fullstory.ts
import { Platform } from 'react-native';

type MaybePromise<T> = T | Promise<T>;

// Minimal interfaces for the parts of the FullStory APIs we use
type RNFullStory = {
  identify: (uid: string, vars?: Record<string, any>) => void;
  event: (name: string, props?: Record<string, any>) => void;
  log?: (message: string, data?: Record<string, any>) => void;
  setUserVars?: (vars: Record<string, any>) => void;
  shutdown?: () => void;
  restart?: () => void;
  consent?: (consented: boolean) => void;
  onReady?: (cb: () => void) => void;
};

type WebFullStory = {
  init?: (opts: { orgId: string; [k: string]: any }) => void;
  identify?: (uid: string, vars?: Record<string, any>) => void;
  event?: (name: string, props?: Record<string, any>) => void;
  log?: (level: string, message?: string, data?: Record<string, any>) => void;
  setUserVars?: (vars: Record<string, any>) => void;
  shutdown?: () => void;
  restart?: () => void;
  consent?: (consented?: boolean) => void;
};

type InitOptions = {
  orgId: string;
  // additional options for web/fullstory init if any
  debug?: boolean;
  [k: string]: any;
};

class FullStoryWrapper {
  private rn?: RNFullStory | null = undefined;
  private web?: WebFullStory | null = undefined;
  private initialized = false;
  private initOpts?: InitOptions;

  private getWebApi(): any | null {
    if (typeof window === 'undefined') return null;

    const namespace = (window as any)._fs_namespace;
    const fsFromNamespace = namespace ? (window as any)[namespace] : undefined;
    const fs = fsFromNamespace || (window as any).FS;

    if (fs) {
      this.web = fs as WebFullStory;
      return fs;
    }

    return (this.web as any) || null;
  }

  // Initialize — call once in app entry (index.js/index.tsx / App.tsx)
  init(opts: InitOptions): MaybePromise<void> {
    this.initOpts = opts;

    if (this.initialized) {
      // Web snippet may not have been ready during first init.
      if (Platform.OS === 'web' && !this.web) {
        this.initWeb(opts);
      }
      return;
    }

    if (Platform.OS === 'web') {
      return this.initWeb(opts);
    } else {
      return this.initRN(opts);
    }
  }

  private initWeb(opts: InitOptions) {
    try {
      // Use window[window._fs_namespace] (defaults to window.FS) from snippet loader (not npm package)
      const fs = this.getWebApi();
      if (fs) {
        this.initialized = true;
        if (opts.debug) console.log('[FullStory] web initialized from snippet, orgId:', opts.orgId);
      } else {
        // FS not available yet, wait for snippet to load
        this.initialized = true;
        this.web = null;
        if (opts.debug) console.warn('[FullStory] window.FS not found - snippet may not be loaded yet');
      }
    } catch (err) {
      this.web = null;
      this.initialized = true;
      if (opts.debug) console.warn('[FullStory] web init failed:', err);
    }
  }

  private initRN(opts: InitOptions) {
    try {
      // use require to avoid static import issues in environments that can't resolve native modules
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const fs = require('@fullstory/react-native').default as RNFullStory;
      this.rn = fs;
      this.initialized = true;
      if (opts.debug) console.log('[FullStory] RN module loaded successfully');
      // FullStory RN typically reads OrgId from native config (Info.plist / AndroidManifest)
      // The OrgId should be configured in:
      // - iOS: Info.plist (FSSessionOrgId)
      // - Android: AndroidManifest.xml (FSSessionOrgId)
      // If the RN package exposes an init/start method, you can call it here.
      if (opts.debug) {
        console.log('[FullStory] Make sure OrgId is configured in native config:');
        console.log('[FullStory] iOS: Info.plist with key FSSessionOrgId');
        console.log('[FullStory] Android: AndroidManifest.xml with metadata FSSessionOrgId');
      }
    } catch (err) {
      this.rn = null;
      this.initialized = true;
      if (opts.debug) {
        console.warn('[FullStory] RN init failed:', err);
        console.warn('[FullStory] Make sure @fullstory/react-native is installed and linked');
      }
    }
  }

  // --- Unified API methods ---

  identify(uid: string, vars?: Record<string, any>) {
    if (!this.initialized) this.initIfNeeded();
    if (Platform.OS === 'web') {
      try {
        // Use window.FS.identify from snippet (FS is an object with methods)
        if (typeof window !== 'undefined' && (window as any).FS && typeof (window as any).FS.identify === 'function') {
          (window as any).FS.identify(uid, vars || {});
          if (this.initOpts?.debug) console.log('[FullStory] web identify executed:', uid, vars);
        } else {
          console.warn('[FullStory] window.FS.identify not available');
        }
      } catch (err) {
        console.error('[FullStory] web identify error:', err);
      }
    } else {
      try {
        // For React Native, use the identify method directly
        this.rn?.identify?.(uid, vars);
      } catch (_) {}
    }
  }

  event(name: string, props?: Record<string, any>) {
    if (!this.initialized) this.initIfNeeded();
    if (Platform.OS === 'web') {
      try {
        const fs = this.getWebApi();
        if (fs?.event && typeof fs.event === 'function') {
          fs.event(name, props);
        } else if (typeof fs === 'function') {
          // Fallback to the low-level API, supported by the snippet stub.
          fs('trackEvent', { name, properties: props });
        } else if (this.initOpts?.debug) {
          console.warn('[FullStory] web event not sent - FS API not available');
        }
      } catch (err) {
        if (this.initOpts?.debug) console.warn('[FullStory] web event error:', err);
      }
    } else {
      try { this.rn?.event?.(name, props); } catch (_) {}
    }
  }

  log(message: string, data?: Record<string, any>) {
    if (!this.initialized) this.initIfNeeded();
    if (Platform.OS === 'web') {
      try {
        const fs = this.getWebApi();
        if (fs?.log && typeof fs.log === 'function') {
          fs.log('info', message, data);
        } else if (typeof fs === 'function') {
          fs('log', { level: 'info', msg: message, data });
        }
      } catch (_) {}
    } else {
      try { this.rn?.log?.(message, data); } catch (_) {}
    }
  }

  setUserVars(vars: Record<string, any>) {
    if (!this.initialized) this.initIfNeeded();
    if (Platform.OS === 'web') {
      try {
        const fs = this.getWebApi();
        if (fs?.setUserVars && typeof fs.setUserVars === 'function') {
          fs.setUserVars(vars);
        } else if (typeof fs === 'function') {
          fs('setProperties', { type: 'user', properties: vars });
        }
      } catch (_) {}
    } else {
      try { this.rn?.setUserVars?.(vars); } catch (_) {}
    }
  }

  shutdown() {
    if (!this.initialized) this.initIfNeeded();
    try {
      if (Platform.OS === 'web') {
        const fs = this.getWebApi();
        fs?.shutdown?.();
      } else {
        this.rn?.shutdown?.();
      }
    } catch (_) {}
  }

  restart() {
    if (!this.initialized) this.initIfNeeded();
    try {
      if (Platform.OS === 'web') {
        const fs = this.getWebApi();
        fs?.restart?.();
      } else {
        this.rn?.restart?.();
      }
    } catch (_) {}
  }

  consent(consented?: boolean) {
    if (!this.initialized) this.initIfNeeded();
    try {
      if (Platform.OS === 'web') {
        const fs = this.getWebApi();
        fs?.consent?.(consented);
      } else {
        this.rn?.consent?.(!!consented);
      }
    } catch (_) {}
  }

  onReady(cb: () => void) {
    if (!this.initialized) this.initIfNeeded();
    try {
      if (Platform.OS === 'web') {
        // Snippet loader doesn't provide a reliable onReady hook; call immediately.
        cb();
      } else {
        this.rn?.onReady?.(cb);
      }
    } catch (_) { cb(); }
  }

  // If user calls any method before init, try to init with stored opts or no-op
  private initIfNeeded() {
    if (!this.initialized) {
      if (this.initOpts) {
        // safe re-init
        // ignore returned promise
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.init(this.initOpts);
      } else {
        // mark initialized to avoid repeated attempts
        this.initialized = true;
        this.rn = this.web = null;
      }
    }
  }
}

const FullStory = new FullStoryWrapper();
export default FullStory;
